import type { Action, Perk, ProjectileAction, AreaAction } from './actions';
import type { Modifier, StatKey } from './stats';
import type { Tag } from './tags';

export type ItemSlot = 'weapon' | 'armor' | 'charm';
/**
 * What a weapon is, so a hero cannot pick up anything at all: heavy two-handers, light blades
 * and claws, bows and guns, staves and other focuses. Heroes allow two classes each.
 */
export type WeaponClass = 'heavy' | 'light' | 'bow' | 'staff';
export const WEAPON_CLASSES: WeaponClass[] = ['heavy', 'light', 'bow', 'staff'];
export type Rarity = 0 | 1 | 2 | 3; // common, rare, epic, legendary
export const RARITY_LEGENDARY: Rarity = 3;

export interface WeaponDef {
  id: string;
  slot: 'weapon';
  /** which heroes may wield it */
  wclass: WeaponClass;
  /** shots per second before attack speed */
  fireRate: number;
  /** multiplier of the wielder's power per hit */
  baseDamage: number;
  attack: ProjectileAction | AreaAction;
  tags: Tag[];
  color: string;
  glyph: string;
  tier: 1 | 2 | 3;
  /** legendary weapons carry a perk like armour does */
  perk?: Perk;
  legendary?: boolean;
}

export interface ArmorDef extends Perk {
  id: string;
  slot: 'armor';
  color: string;
  glyph: string;
  tier: 1 | 2 | 3;
  legendary?: boolean;
}

export interface CharmDef extends Perk {
  id: string;
  slot: 'charm';
  color: string;
  glyph: string;
  tier: 1 | 2 | 3;
  legendary?: boolean;
}

export type ItemDef = WeaponDef | ArmorDef | CharmDef;

export interface ItemInst {
  uid: number;
  def: string;
  rarity: Rarity;
  affixes: Modifier[];
}

export const WEAPONS: Record<string, WeaponDef> = {
  claymore: {
    id: 'claymore', slot: 'weapon', wclass: 'heavy', fireRate: 1.1, baseDamage: 1.9, tags: ['slash'], color: '#c9d1dd', glyph: '⚔', tier: 1,
    attack: { type: 'area', at: 'melee', radius: 0, arc: 2.2, range: 105, damage: 1, tags: ['slash'], visual: 'swing', knockback: 140 },
  },
  longbow: {
    id: 'longbow', slot: 'weapon', wclass: 'bow', fireRate: 2.6, baseDamage: 0.75, tags: ['pierce', 'projectile'], color: '#b9a37a', glyph: '➹', tier: 1,
    attack: { type: 'projectile', count: 1, spread: 0.12, pattern: 'aimed', speed: 760, range: 720, damage: 1, radius: 6, pierce: 1, tags: ['pierce', 'projectile'], visual: 'arrow' },
  },
  staff: {
    id: 'staff', slot: 'weapon', wclass: 'staff', fireRate: 1.7, baseDamage: 1.0, tags: ['void', 'projectile'], color: '#9d7dff', glyph: '⚚', tier: 1,
    attack: { type: 'projectile', count: 1, spread: 0.2, pattern: 'aimed', speed: 430, range: 640, damage: 1, radius: 8, homing: 3, tags: ['void', 'projectile'], visual: 'bolt_void' },
  },
  crossbow: {
    id: 'crossbow', slot: 'weapon', wclass: 'bow', fireRate: 1.0, baseDamage: 1.1, tags: ['pierce', 'projectile'], color: '#8c7a5a', glyph: '✢', tier: 2,
    attack: { type: 'projectile', count: 3, spread: 0.45, pattern: 'aimed', speed: 700, range: 620, damage: 1, radius: 6, pierce: 2, tags: ['pierce', 'projectile'], visual: 'arrow' },
  },
  flail: {
    id: 'flail', slot: 'weapon', wclass: 'heavy', fireRate: 1.4, baseDamage: 1.4, tags: ['blunt'], color: '#a8a8a8', glyph: '⛓', tier: 2,
    attack: { type: 'area', at: 'melee', radius: 0, arc: 3.6, range: 95, damage: 1, tags: ['blunt'], visual: 'swing_blunt', knockback: 260, status: [{ id: 'stun', duration: 0.25 }] },
  },
  fire_wand: {
    id: 'fire_wand', slot: 'weapon', wclass: 'staff', fireRate: 1.5, baseDamage: 0.9, tags: ['fire', 'projectile'], color: '#ff8a3c', glyph: '🜂', tier: 2,
    attack: { type: 'projectile', count: 1, spread: 0.15, pattern: 'aimed', speed: 520, range: 600, damage: 1, radius: 8, tags: ['fire', 'projectile'], visual: 'ember', status: [{ id: 'burn', duration: 2, stacks: 1 }], onHit: { type: 'area', at: 'target', radius: 40, damage: 0.4, tags: ['fire', 'area'], visual: 'burst_fire' } },
  },
  twin_daggers: {
    id: 'twin_daggers', slot: 'weapon', wclass: 'light', fireRate: 3.6, baseDamage: 0.55, tags: ['slash', 'bleed'], color: '#e5e5e5', glyph: '🗡', tier: 2,
    attack: { type: 'area', at: 'melee', radius: 0, arc: 1.4, range: 70, damage: 1, tags: ['slash', 'bleed'], visual: 'swing_fast', status: [{ id: 'bleed', duration: 3, stacks: 1 }] },
  },
  frost_staff: {
    id: 'frost_staff', slot: 'weapon', wclass: 'staff', fireRate: 1.6, baseDamage: 0.95, tags: ['frost', 'projectile'], color: '#9fdcff', glyph: '❄', tier: 2,
    attack: { type: 'projectile', count: 1, spread: 0.15, pattern: 'aimed', speed: 500, range: 640, damage: 1, radius: 8, pierce: 1, tags: ['frost', 'projectile'], visual: 'shard_frost', status: [{ id: 'chill', duration: 3, stacks: 1 }] },
  },
  storm_bow: {
    id: 'storm_bow', slot: 'weapon', wclass: 'bow', fireRate: 2.2, baseDamage: 0.8, tags: ['lightning', 'pierce', 'projectile'], color: '#b9ecff', glyph: '⚡', tier: 3,
    attack: { type: 'projectile', count: 2, spread: 0.3, pattern: 'aimed', speed: 820, range: 760, damage: 1, radius: 6, pierce: 1, tags: ['lightning', 'pierce', 'projectile'], visual: 'arrow_storm', status: [{ id: 'shock', duration: 3, stacks: 1 }] },
  },
  doom_blade: {
    id: 'doom_blade', slot: 'weapon', wclass: 'heavy', fireRate: 1.0, baseDamage: 2.4, tags: ['slash', 'void'], color: '#7a3cff', glyph: '⚔', tier: 3,
    attack: { type: 'area', at: 'melee', radius: 0, arc: 2.6, range: 120, damage: 1, tags: ['slash', 'void'], visual: 'swing_void', knockback: 120, status: [{ id: 'vulnerable', duration: 2 }] },
  },
  // weapons of the unlockable heroes
  halberd: {
    id: 'halberd', slot: 'weapon', wclass: 'heavy', fireRate: 1.0, baseDamage: 1.8, tags: ['slash', 'blunt'], color: '#b8c4d8', glyph: '⚚', tier: 1,
    attack: { type: 'area', at: 'melee', radius: 0, arc: 2.0, range: 125, damage: 1, tags: ['slash', 'blunt'], visual: 'swing', knockback: 160, status: [{ id: 'weak', duration: 1.5 }] },
  },
  soul_orb: {
    id: 'soul_orb', slot: 'weapon', wclass: 'staff', fireRate: 1.8, baseDamage: 0.9, tags: ['void', 'projectile'], color: '#ff6ad5', glyph: '●', tier: 1,
    attack: { type: 'projectile', count: 1, spread: 0, pattern: 'aimed', speed: 460, range: 560, damage: 1, radius: 8, homing: 3, tags: ['void', 'projectile'], visual: 'bolt_dark' },
  },
  claws: {
    id: 'claws', slot: 'weapon', wclass: 'light', fireRate: 2.6, baseDamage: 0.7, tags: ['slash', 'bleed'], color: '#ffb347', glyph: '⟑', tier: 1,
    attack: { type: 'area', at: 'melee', radius: 0, arc: 1.6, range: 80, damage: 1, tags: ['slash', 'bleed'], visual: 'swing_fast', status: [{ id: 'bleed', duration: 2, stacks: 1 }] },
  },
  sickle: {
    id: 'sickle', slot: 'weapon', wclass: 'light', fireRate: 1.4, baseDamage: 1.2, tags: ['slash'], color: '#8fe38a', glyph: '☾', tier: 1,
    attack: { type: 'area', at: 'melee', radius: 0, arc: 2.4, range: 95, damage: 1, tags: ['slash'], visual: 'swing', knockback: 90 },
  },
  totem_staff: {
    id: 'totem_staff', slot: 'weapon', wclass: 'staff', fireRate: 2.0, baseDamage: 0.85, tags: ['projectile'], color: '#ff8c42', glyph: '⚕', tier: 1,
    attack: { type: 'projectile', count: 1, spread: 0, pattern: 'aimed', speed: 600, range: 620, damage: 1, radius: 7, tags: ['projectile'], visual: 'bolt_totem' },
  },
  sparkgun: {
    id: 'sparkgun', slot: 'weapon', wclass: 'bow', fireRate: 2.4, baseDamage: 0.7, tags: ['void', 'projectile'], color: '#a7ff5c', glyph: '⌇', tier: 1,
    attack: { type: 'projectile', count: 1, spread: 0.08, pattern: 'aimed', speed: 700, range: 430, damage: 1, radius: 6, tags: ['void', 'projectile'], visual: 'spark' },
  },
  lute: {
    id: 'lute', slot: 'weapon', wclass: 'staff', fireRate: 2.4, baseDamage: 0.75, tags: ['holy', 'projectile'], color: '#ffd166', glyph: '♫', tier: 1,
    attack: { type: 'projectile', count: 1, spread: 0.1, pattern: 'aimed', speed: 640, range: 560, damage: 1, radius: 7, tags: ['holy', 'projectile'], visual: 'note' },
  },
};

export const ARMORS: Record<string, ArmorDef> = {
  chain_mail: { id: 'chain_mail', slot: 'armor', color: '#a9b4c2', glyph: '▤', tier: 1, mods: [{ stat: 'armor', add: 12 }] },
  leather: { id: 'leather', slot: 'armor', color: '#a5754a', glyph: '▥', tier: 1, mods: [{ stat: 'moveSpeed', inc: 0.08 }, { stat: 'critChance', add: 0.05 }, { stat: 'armor', add: 4 }] },
  robe: { id: 'robe', slot: 'armor', color: '#7d6bd6', glyph: '▧', tier: 1, mods: [{ stat: 'cooldown', inc: -0.12 }, { stat: 'maxHp', inc: -0.05 }] },
  plate: { id: 'plate', slot: 'armor', color: '#d7dde6', glyph: '▦', tier: 2, mods: [{ stat: 'armor', add: 28 }, { stat: 'moveSpeed', inc: -0.1 }] },
  mirror_plate: { id: 'mirror_plate', slot: 'armor', color: '#eef4ff', glyph: '▩', tier: 2, mods: [{ stat: 'thorns', add: 0.3 }, { stat: 'armor', add: 10 }] },
  bone_armor: { id: 'bone_armor', slot: 'armor', color: '#efe6d2', glyph: '☗', tier: 2, mods: [{ stat: 'summonPower', inc: 0.3 }, { stat: 'summonCount', add: 1 }] },
  ashen_mantle: { id: 'ashen_mantle', slot: 'armor', color: '#ff9a5c', glyph: '▨', tier: 3, mods: [{ stat: 'armor', add: 15 }, { stat: 'statusPower', inc: 0.25 }], hooks: [{ on: 'damaged', cooldown: 2, action: { type: 'area', at: 'self', radius: 90, damage: 0.8, tags: ['fire', 'area'], status: [{ id: 'burn', duration: 2, stacks: 1 }], visual: 'burst_fire' } }] },
};

export const CHARMS: Record<string, CharmDef> = {
  amulet_regen: { id: 'amulet_regen', slot: 'charm', color: '#7be0a0', glyph: '❁', tier: 1, mods: [{ stat: 'hpRegen', add: 2 }, { stat: 'maxHp', inc: 0.08 }] },
  amulet_crit: { id: 'amulet_crit', slot: 'charm', color: '#ffd1e0', glyph: '✦', tier: 1, mods: [{ stat: 'critChance', add: 0.1 }, { stat: 'critMult', add: 0.25 }] },
  amulet_greed: { id: 'amulet_greed', slot: 'charm', color: '#ffd166', glyph: '◉', tier: 1, mods: [{ stat: 'luck', add: 2 }, { stat: 'xpGain', inc: 0.12 }] },
  amulet_haste: { id: 'amulet_haste', slot: 'charm', color: '#a6f0ff', glyph: '⟫', tier: 2, mods: [{ stat: 'attackSpeed', inc: 0.15 }, { stat: 'cooldown', inc: -0.08 }] },
  amulet_wrath: { id: 'amulet_wrath', slot: 'charm', color: '#ff5c5c', glyph: '♦', tier: 2, hooks: [{ on: 'lowhp', hpBelow: 0.4, cooldown: 8, action: { type: 'buff', target: 'self', status: [{ id: 'might', duration: 6 }, { id: 'haste', duration: 6 }] } }], mods: [{ stat: 'damage', inc: 0.08 }] },
  amulet_summons: { id: 'amulet_summons', slot: 'charm', color: '#c8b8ff', glyph: '♆', tier: 2, mods: [{ stat: 'summonPower', inc: 0.25 }, { stat: 'summonCount', add: 1 }] },
  amulet_storm: { id: 'amulet_storm', slot: 'charm', color: '#c9f3ff', glyph: '⚡', tier: 3, hooks: [{ on: 'hit', chance: 0.12, cooldown: 0.5, action: { type: 'chain', jumps: 3, range: 200, damage: 0.7, tags: ['lightning'], status: [{ id: 'shock', duration: 3 }], visual: 'chain' } }] },
  amulet_lich: { id: 'amulet_lich', slot: 'charm', color: '#8fd66a', glyph: '☠', tier: 3, hooks: [{ on: 'kill', chance: 0.15, cooldown: 1, action: { type: 'summon', unit: 'skeleton', count: 1, duration: 12, at: 'self' } }], mods: [{ stat: 'summonPower', inc: 0.15 }] },
};

/**
 * Legendaries: unique rules rather than bigger numbers. They never roll from the ordinary pools;
 * bosses, deep elites and deep chests have a small chance to drop one, announced to the realm.
 */
export const LEGENDARIES: Record<string, ItemDef> = {
  sunspear: {
    id: 'sunspear', slot: 'weapon', wclass: 'bow', fireRate: 2.0, baseDamage: 1.05, tags: ['holy', 'pierce', 'projectile'], color: '#fff2b3', glyph: '☀', tier: 3, legendary: true,
    attack: { type: 'projectile', count: 1, spread: 0, pattern: 'aimed', speed: 900, range: 820, damage: 1, radius: 7, pierce: 3, tags: ['holy', 'pierce', 'projectile'], visual: 'arrow_light' },
    perk: { mods: [{ stat: 'critChance', add: 0.1 }], hooks: [{ on: 'crit', cooldown: 0.4, action: { type: 'area', at: 'target', radius: 70, damage: 0.8, tags: ['holy', 'area'], visual: 'burst_holy' } }] },
  },
  grave_edge: {
    id: 'grave_edge', slot: 'weapon', wclass: 'heavy', fireRate: 1.1, baseDamage: 1.9, tags: ['slash', 'void'], color: '#8fd66a', glyph: '⚰', tier: 3, legendary: true,
    attack: { type: 'area', at: 'melee', radius: 0, arc: 2.4, range: 110, damage: 1, tags: ['slash', 'void'], visual: 'swing_void', knockback: 120 },
    perk: { mods: [{ stat: 'summonPower', inc: 0.3 }], flags: ['necromancer'] },
  },
  frostbrand: {
    id: 'frostbrand', slot: 'weapon', wclass: 'staff', fireRate: 1.6, baseDamage: 1.0, tags: ['frost', 'projectile'], color: '#dff6ff', glyph: '❆', tier: 3, legendary: true,
    attack: { type: 'projectile', count: 2, spread: 0.25, pattern: 'aimed', speed: 520, range: 660, damage: 1, radius: 8, pierce: 1, tags: ['frost', 'projectile'], visual: 'shard_frost', status: [{ id: 'chill', duration: 3, stacks: 1 }] },
    perk: { mods: [{ stat: 'statusPower', inc: 0.2 }], flags: ['allChill', 'shatter'] },
  },
  stormheart: {
    id: 'stormheart', slot: 'weapon', wclass: 'bow', fireRate: 2.4, baseDamage: 0.75, tags: ['lightning', 'pierce', 'projectile'], color: '#c9f3ff', glyph: '⚡', tier: 3, legendary: true,
    attack: { type: 'projectile', count: 3, spread: 0.4, pattern: 'aimed', speed: 860, range: 760, damage: 1, radius: 6, pierce: 1, tags: ['lightning', 'pierce', 'projectile'], visual: 'arrow_storm', status: [{ id: 'shock', duration: 3, stacks: 1 }] },
    perk: { hooks: [{ on: 'hit', chance: 0.2, cooldown: 0.4, action: { type: 'chain', jumps: 4, range: 220, damage: 0.8, tags: ['lightning'], status: [{ id: 'shock', duration: 3 }], visual: 'chain' } }] },
  },
  heart_of_oak: { id: 'heart_of_oak', slot: 'armor', color: '#8fe38a', glyph: '❦', tier: 3, legendary: true, mods: [{ stat: 'maxHp', inc: 0.25 }, { stat: 'hpRegen', add: 3 }], hooks: [{ on: 'lowhp', hpBelow: 0.3, cooldown: 25, action: { type: 'heal', target: 'self', pct: 0.3, visual: 'heal' } }] },
  mirror_carapace: { id: 'mirror_carapace', slot: 'armor', color: '#f4f8ff', glyph: '▩', tier: 3, legendary: true, mods: [{ stat: 'armor', add: 30 }, { stat: 'thorns', add: 0.2 }], flags: ['thornsScaleArmor'] },
  shroud_of_night: { id: 'shroud_of_night', slot: 'armor', color: '#5a4a8a', glyph: '☾', tier: 3, legendary: true, mods: [{ stat: 'dashCharges', add: 1 }, { stat: 'dashCooldown', inc: -0.25 }, { stat: 'moveSpeed', inc: 0.08 }], flags: ['dashResetOnKill'] },
  crown_of_ash: { id: 'crown_of_ash', slot: 'charm', color: '#ff8a3c', glyph: '♕', tier: 3, legendary: true, mods: [{ stat: 'statusPower', inc: 0.3 }], flags: ['killExplode'] },
  idol_of_legion: { id: 'idol_of_legion', slot: 'charm', color: '#c8b8ff', glyph: '♆', tier: 3, legendary: true, mods: [{ stat: 'summonCount', add: 2 }, { stat: 'summonPower', inc: 0.4 }] },
  gambler_coin: { id: 'gambler_coin', slot: 'charm', color: '#ffd166', glyph: '◉', tier: 3, legendary: true, mods: [{ stat: 'luck', add: 6 }, { stat: 'xpGain', inc: 0.1 }], flags: ['lootDouble'] },
  wailing_horn: { id: 'wailing_horn', slot: 'charm', color: '#8a7cff', glyph: '♆', tier: 3, legendary: true, mods: [{ stat: 'cooldown', inc: -0.15 }], hooks: [{ on: 'cast', cooldown: 3, action: { type: 'buff', target: 'enemies', at: 'self', radius: 220, status: [{ id: 'weak', duration: 2.5 }], visual: 'terror' } }] },
};
export const LEGENDARY_IDS = Object.keys(LEGENDARIES);

export const ITEMS: Record<string, ItemDef> = { ...WEAPONS, ...ARMORS, ...CHARMS, ...LEGENDARIES };

export function isLegendary(item: ItemInst): boolean {
  return item.rarity === 3 || !!ITEMS[item.def]?.legendary;
}

export function weaponClassOf(id: string): WeaponClass {
  return weaponDefOf(id).wclass;
}

export function weaponDefOf(id: string): WeaponDef {
  const def = ITEMS[id];
  return def && def.slot === 'weapon' ? def : WEAPONS.claymore;
}

interface AffixTemplate {
  stat: StatKey;
  kind: 'add' | 'inc';
  min: number;
  max: number;
}

export const AFFIXES: AffixTemplate[] = [
  { stat: 'maxHp', kind: 'add', min: 10, max: 30 },
  { stat: 'armor', kind: 'add', min: 4, max: 12 },
  { stat: 'damage', kind: 'inc', min: 0.05, max: 0.15 },
  { stat: 'attackSpeed', kind: 'inc', min: 0.05, max: 0.15 },
  { stat: 'moveSpeed', kind: 'inc', min: 0.04, max: 0.1 },
  { stat: 'critChance', kind: 'add', min: 0.03, max: 0.08 },
  { stat: 'cooldown', kind: 'inc', min: -0.12, max: -0.04 },
  { stat: 'area', kind: 'inc', min: 0.08, max: 0.2 },
  { stat: 'lifesteal', kind: 'add', min: 0.02, max: 0.05 },
  { stat: 'thorns', kind: 'add', min: 0.05, max: 0.15 },
  { stat: 'summonPower', kind: 'inc', min: 0.08, max: 0.2 },
  { stat: 'xpGain', kind: 'inc', min: 0.05, max: 0.15 },
  { stat: 'luck', kind: 'add', min: 1, max: 3 },
  { stat: 'hpRegen', kind: 'add', min: 0.5, max: 2 },
  { stat: 'statusPower', kind: 'inc', min: 0.08, max: 0.2 },
];

export function itemPerk(item: ItemInst): Perk {
  const def = ITEMS[item.def];
  const perk: Perk = { mods: [...item.affixes], hooks: [], flags: [] };
  const src: Perk | undefined = def ? (def.slot === 'weapon' ? def.perk : def) : undefined;
  if (src) {
    if (src.mods) perk.mods!.push(...src.mods);
    if (src.hooks) perk.hooks!.push(...src.hooks);
    if (src.flags) perk.flags!.push(...src.flags);
  }
  return perk;
}

export function weaponAction(item: ItemInst): Action {
  return weaponDefOf(item.def).attack;
}
